import React, { useState, useEffect } from 'react';
import { Smartphone, X, Share } from 'lucide-react';

const STORAGE_KEY = 'storeops:ios_install_dismissed_v1';

/**
 * iOS install banner.
 *
 * Shown only when ALL of the following are true:
 *   - The page is opened in iOS Safari (not Chrome iOS / Firefox iOS,
 *     because they cannot install the PWA — only Safari can).
 *   - The page is NOT already running as a standalone PWA
 *     (`navigator.standalone === true` means the user has already added it
 *     to the home screen).
 *   - The user has not dismissed the banner in this browser yet
 *     (localStorage flag).
 *
 * Strategy: a sticky bottom strip with concise copy and the standard
 * iOS share icon so users instantly recognise the gesture they need to
 * perform. Dismiss persists across sessions per browser.
 */
export default function IOSInstallBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
    try {
      if (window.localStorage.getItem(STORAGE_KEY) === '1') return;
    } catch { /* private mode → just show it */ }

    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/.test(ua);
    // iOS Chrome/Firefox UAs include CriOS/FxiOS; they CANNOT install PWAs.
    const isOtherIOSBrowser = /CriOS|FxiOS|EdgiOS/i.test(ua);
    const isStandalone =
      window.navigator.standalone === true ||
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);

    if (isIOS && !isOtherIOSBrowser && !isStandalone) {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch { /* private mode */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-slate-900 text-white px-4 py-3 app-safe-bottom shadow-[0_-4px_20px_rgba(0,0,0,0.15)] flex items-start gap-3"
      data-testid="ios-install-banner"
      role="dialog"
      aria-label="Installa StoreOps come app"
    >
      <div className="w-9 h-9 bg-white text-slate-900 flex items-center justify-center flex-shrink-0">
        <Smartphone className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight">Installa StoreOps sull'iPhone</p>
        <p className="text-[12px] text-slate-300 leading-snug mt-1">
          Tappa{' '}
          <Share className="w-3.5 h-3.5 inline-block mx-0.5 -mt-0.5" strokeWidth={2.5} />
          <span className="font-semibold text-white">Condividi</span> in basso, poi{' '}
          <span className="font-semibold text-white">"Aggiungi a Home"</span>.
        </p>
      </div>
      <button
        onClick={dismiss}
        className="p-1 -mr-1 -mt-1 text-slate-400 hover:text-white transition-colors flex-shrink-0"
        data-testid="ios-install-banner-dismiss"
        aria-label="Chiudi"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
